import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";

export default function Legal({ kind }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setContent(null);
    setError(false);
    api.get(`/legal/doc/${kind}`)
      .then(({ data }) => { if (active) setContent(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [kind]);

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid={`legal-page-${kind}`}>
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-link"><Logo /></Link>
          <Link to="/" data-testid="legal-home-link" className="flex items-center gap-1.5 text-sm text-[#475569] hover:text-brand">
            <ArrowLeft size={15} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {error ? (
          <p className="text-[#B91C1C]" data-testid="legal-error">Couldn't load this page. Please try again later.</p>
        ) : !content ? (
          <div className="space-y-4"><Skeleton className="h-10 w-1/2 rounded-lg" /><Skeleton className="h-40 rounded-xl" /><Skeleton className="h-40 rounded-xl" /></div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-bold tracking-tight text-navy" data-testid="legal-title">{content.title}</h1>
            <p className="mt-2 text-sm text-[#94A3B8]" data-testid="legal-meta">
              {content.version ? <>Version {content.version} · </> : null}
              {content.effective_date ? <>Effective {content.effective_date} · </> : null}
              Last updated: {content.updated}
            </p>
            <p className="mt-5 text-base text-[#334155] leading-relaxed">{content.intro}</p>
            <div className="mt-8 space-y-6">
              {content.sections.map((s, i) => (
                <section key={i} data-testid={`legal-section-${i}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6">
                  <h2 className="font-heading text-lg font-semibold text-navy">{s.heading}</h2>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed whitespace-pre-line">{s.body}</p>
                </section>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <Link to="/privacy" className="text-[#475569] hover:text-brand underline">Privacy</Link>
              <Link to="/terms" className="text-[#475569] hover:text-brand underline">Terms</Link>
              <Link to="/ai-limitations" className="text-[#475569] hover:text-brand underline">AI & Evidence Limitations</Link>
              <Link to="/data-deletion" className="text-[#475569] hover:text-brand underline">Data Deletion</Link>
              <Link to="/acceptable-use" className="text-[#475569] hover:text-brand underline">Acceptable Use</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
